import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";

export default function About() {
  const [page, setPage] = useState(null);

  useEffect(() => {
    api.get("/pages/about").then((r) => setPage(r.data)).catch(() => {});
  }, []);

  if (!page) {
    return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;
  }

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--ink-hairline)]">
        <div className="pointer-events-none absolute inset-0">
          <img
            src={page.hero_image || IMG.forest}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "sepia(0.2) saturate(0.55) contrast(1.02) brightness(1.05)" }}
          />
          <div className="absolute inset-0 hero-scrim" />
        </div>
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-24 lg:px-8 lg:pb-32 lg:pt-32">
          <div className="pl-6 lg:pl-10">
            <div className="vline absolute left-5 top-24 h-20 w-[2px] lg:left-8 lg:top-32" />
            <p className="overline mb-6" data-testid="about-kicker">{page.hero_kicker || "About"}</p>
            <h1
              data-testid="about-title"
              className="max-w-3xl font-editorial text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              {page.hero_title}
            </h1>
          </div>
        </div>
      </section>

      {/* About HK Forexedge intro */}
      {page.hero_subtitle && (
        <section className="border-b border-[color:var(--ink-hairline)] bg-[color:var(--paper-soft)]">
          <div className="mx-auto max-w-4xl px-5 py-20 lg:px-8 lg:py-24">
            <p className="overline mb-4" data-testid="about-intro-kicker">About HK Forexedge</p>
            <p
              data-testid="about-intro-body"
              className="font-editorial text-2xl leading-relaxed tracking-tight text-[color:var(--ink)] sm:text-3xl"
            >
              {page.hero_subtitle}
            </p>
          </div>
        </section>
      )}

      {/* Sections */}
      <section className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
        <div className="grid gap-16 lg:gap-24">
          {page.sections?.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6 }}
                className={`grid items-center gap-10 lg:gap-16 ${
                  s.image_url ? "lg:grid-cols-2" : "lg:grid-cols-[1.4fr_1fr]"
                }`}
              >
                <div className={flip && s.image_url ? "lg:order-2" : ""}>
                  <p className="overline mb-4">0{i + 1} / section</p>
                  <h3 className="font-editorial text-3xl font-medium leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
                    {s.heading}
                  </h3>
                  <div className="prose-edge mt-6 max-w-xl">
                    <p>{s.body}</p>
                  </div>
                </div>
                {s.image_url && (
                  <div className="photo-vignette h-[320px] lg:h-[440px]">
                    <img src={s.image_url} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="mt-24 flex flex-wrap gap-3">
          <Link to="/store/expert-advisors" className="btn-edge">
            Explore Expert Advisors <ArrowUpRight size={14} />
          </Link>
          <Link to="/contact" className="btn-edge-ghost">Contact us</Link>
        </div>
      </section>
    </div>
  );
}
